// conditionReportCorrection — the staff words for a condition report's
// CORRECTED VERSION ("Request new signatures", docs/condition-reports-system.md
// 2026-10-06), kept apart from ItemPage so they are assertable without a DOM.
//
// Every string here is staff-facing and names people and dates only — never a
// submission or document id (docs/no-ids-in-ui.md). New copy is marked
// PENDING MILES COPY REVIEW.

import type { ConditionReportCorrectionPanel } from './sentItemsClient';

/** "October 6, 2026" — a whole date, said the same way on every machine. */
export function formatCorrectionDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// PENDING MILES COPY REVIEW — every constant below.
export const CORRECTION_COPY = {
  requestButton: 'Request new signatures',
  requestTitle: 'Request new signatures?',
  requestConsequence:
    'A corrected copy is made from the current zInspector report and sent to the residents to sign. ' +
    'The signatures already on the first copy stay in place: the corrected copy replaces it once ' +
    'every resident has signed it. Gary is asked to countersign each copy in turn.',
  requestConfirm: 'Send the corrected copy',
  whatChangedLabel: 'What changed (the residents will see this)',
  whatChangedPlaceholder: 'Optional. For example: the inspection photos are now included.',
  pendingLine:
    'A corrected version is out for signature; it replaces the first one once the residents sign it.',
  withdrawButton: 'Withdraw the corrected version',
  finishButton: 'Finish sending the corrected version',
  finishHint: 'The corrected version has not reached every resident yet.',
  resentToast: 'The corrected version has been sent to everyone it had not reached.',
  unknownLine:
    'Couldn’t confirm the withdrawal in DocuSeal — check it there, then mark it withdrawn or put it back.',
  markWithdrawnButton: 'Mark it withdrawn',
  putBackButton: 'Put it back',
  putBackToast: 'The corrected version is back out for signature.',
  withdrawTitle: 'Withdraw the corrected version?',
  withdrawConsequence:
    'The corrected copy is withdrawn and its signing links stop working. The first copy stands again, ' +
    'with its signatures — and if it was already countersigned, it is completed now.',
  withdrawConfirm: 'Withdraw it',
  requestedToast: 'Corrected version sent for signature.',
  withdrawnToast: 'Corrected version withdrawn. The first copy stands.',
} as const;

/** Longest "what changed" note the residents can be shown (the API enforces it too). */
export const WHAT_CHANGED_MAX = 300;

/** The "who asked, when" line under the pending notice. */
export function correctionRequestedLine(panel: ConditionReportCorrectionPanel): string {
  const when = formatCorrectionDate(panel.requestedAt);
  const by = panel.requestedBy?.trim();
  // PENDING MILES COPY REVIEW
  return by && when ? `Requested by ${by} on ${when}` : by ? `Requested by ${by}` : when ? `Requested on ${when}` : '';
}

/** What the residents were told changed, when staff wrote it. */
export function correctionWhatChangedLine(panel: ConditionReportCorrectionPanel): string {
  const note = panel.whatChanged?.trim();
  // PENDING MILES COPY REVIEW
  return note ? `The residents were told: “What changed: ${note}”` : '';
}

/** "Corrected version took effect on October 6, 2026" (spec wording). */
export function correctionTookEffectLine(panel: ConditionReportCorrectionPanel): string {
  const when = formatCorrectionDate(panel.tookEffectAt);
  // PENDING MILES COPY REVIEW
  return when ? `Corrected version took effect on ${when}` : 'Corrected version took effect';
}

/** The toast after a request: how many residents were emailed. */
export function correctionRequestedToast(emailed: number | undefined): string {
  // PENDING MILES COPY REVIEW
  if (!emailed) return CORRECTION_COPY.requestedToast;
  return `${CORRECTION_COPY.requestedToast} ${emailed === 1 ? 'One resident was' : `${emailed} residents were`} emailed.`;
}
